import React, { useCallback, useContext, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';
import { css } from '@compiled/react';

import Tabs, { TabList, TabPanel, useTab } from '@atlaskit/tabs';
import { Box, Stack, Inline, Text, Pressable } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { cssMap, cx } from '@atlaskit/css';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { fg } from '@confluence/feature-gating';
import { AI_AUDIO_BRIEFING } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers';

import { useAudioStream } from '../../hooks/useAudioStream';
import { useAudioEventListener } from '../../hooks/useAudioEventListener';
import { useAudioBriefing } from '../../hooks/useAudioBriefing';
import { AudioTimeIndicator } from '../controls/AudioTimeIndicator';
import { getSharedAttributes } from '../../utils/getSharedAttributes';
import type { CustomLengthType, CustomToneType } from '../../types/customizationTypes';
import { CustomLength, CustomTone } from '../../types/customizationTypes';
import {
	getAudioLengthMs,
	getAudioCurrentTimeMs,
	getAudioProgress,
} from '../../utils/audioTimeUtils';
import { AudioFeature } from '../../types/audioFeatureType';

import { AudioBriefingChangeboarding } from './AudioBriefingChangeboarding';
import { AudioBriefingPopupControlButton } from './AudioBriefingPopupControlButton';
import { AudioBriefingPopupListenPanel } from './panels/listen/AudioBriefingPopupListenPanel';
import { AudioBriefingPopupCustomizePanel } from './panels/customize/AudioBriefingPopupCustomizePanel';
import { AudioBriefingPopupSourcesPanel } from './panels/sources/AudioBriefingPopupSourcesPanel';
import { AudioBriefingPopupFooter } from './AudioBriefingPopupFooter';
import { AudioBriefingErrorComponent } from './AudioBriefingErrorComponent';
import { AI_AUDIO_BRIEFING_INTERACTION_METRIC } from './perf.config';

const i18n = defineMessages({
	listenTab: {
		id: 'audio.audio-briefing-popup.listen-tab',
		defaultMessage: 'Listen',
		description:
			'The name of the tab in the audio briefing popup that directs towards the miniplayer to listen to audio',
	},
	customizeTab: {
		id: 'audio.audio-briefing-popup.customize-tab',
		defaultMessage: 'Customize',
		description:
			'The name of the tab in the audio briefing popup that directs towards the customization panel',
	},
	sourcesTab: {
		id: 'audio.audio-briefing-popup.sources-tab',
		defaultMessage: 'Sources',
		description:
			'The name of the tab in the audio briefing popup that directs towards the sources panel',
	},
	briefingTitle: {
		id: 'audio.audio-briefing-popup.briefing-title',
		defaultMessage: 'Briefing: {contentTitle}',
		description: 'Title of the audio briefing popup which takes in the actual page title',
	},
	closeButtonLabel: {
		id: 'audio.audio-briefing-popup.close-label',
		defaultMessage: 'Close',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will close the audio briefings popup',
	},
	briefingBylineTemplate: {
		id: 'audio.audio-briefing-popup.briefing-byline-template',
		// defaultMessage: '{length} briefing • {tone} tone',
		defaultMessage: '{length} {tone} overview',
		description: 'Template for the byline that displays length and tone of the briefing',
	},
	lengthShort: {
		id: 'audio.audio-briefing-popup.length-short',
		defaultMessage: 'Concise',
		description: 'Label for short (concise) briefing length option',
	},
	lengthMedium: {
		id: 'audio.audio-briefing-popup.length-medium',
		defaultMessage: 'Medium length',
		description: 'Label for medium length briefing option',
	},
	lengthLong: {
		id: 'audio.audio-briefing-popup.length-long',
		defaultMessage: 'Extended',
		description: 'Label for long (extended) briefing length option',
	},
	toneProfessional: {
		id: 'audio.audio-briefing-popup.tone-professional',
		defaultMessage: 'professional',
		description: 'Label for professional tone option',
	},
	toneConversational: {
		id: 'audio.audio-briefing-popup.tone-conversational',
		defaultMessage: 'conversational',
		description: 'Label for conversational tone option',
	},
	tonePlayful: {
		id: 'audio.audio-briefing-popup.tone-playful',
		defaultMessage: 'playful',
		description: 'Label for playful tone option',
	},
	toneSurprise: {
		id: 'audio.audio-briefing-popup.tone-surprise',
		defaultMessage: 'surprise me',
		description: 'Label for surprise tone option',
	},
});

type AudioBriefingPopupContentProps = {
	closePopup: (additionalAttributes?: Record<string, any>) => void;
	isPopupMinimized: boolean;
	setIsPopupMinimized: (isMinimized: boolean) => void;
	showChangeboarding: boolean;
	handleCloseChangeboarding: () => void;
};

type AudioBriefingPopupContentComponentProps = AudioBriefingPopupContentProps & {
	audioRef: React.RefObject<HTMLAudioElement>;
};

type MinimizedPlayerProps = {
	expandPopup: () => void;
	audioRef: React.RefObject<HTMLAudioElement>;
	isPlaying: boolean;
	isLoading: boolean;
};

const tabsListStyles = css({
	marginBottom: token('space.150'),
	marginTop: token('space.150'),
	padding: token('space.050'),
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div[role=tablist]': {
		marginInlineStart: 'unset',
		gap: token('space.050'),
		display: 'flex',
		width: '100%',
		'&::before': {
			all: 'unset',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[role=tab]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&[aria-selected=true]::after': {
				insetInlineStart: 'unset',
			},
			'&:hover::after': {
				insetInlineStart: 'unset',
			},
		},
	},
});

const styles = cssMap({
	// These styles are used to style the popup to be in parity with Atlaskit's default popup styles
	popupStyles: {
		boxSizing: 'border-box',
		zIndex: 400,
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		overflow: 'hidden',
	},
	wrapper: {
		width: '300px',
	},
	footer: {
		backgroundColor: token('elevation.surface.sunken'),
	},
	headerTitle: {
		flex: 1,
	},
	minimizedWrapper: {
		width: '105px',
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
	},
	customTab: {
		paddingBlock: token('space.025'),
		borderRadius: token('radius.small'),
		flex: 1,
		backgroundColor: token('elevation.surface'),
		fontWeight: token('font.weight.medium'),
		font: token('font.body.small'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	selectedTab: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
	},
});

const AUDIO_TABS = [
	{ key: 'listen', label: i18n.listenTab },
	{ key: 'sources', label: i18n.sourcesTab },
	{ key: 'customize', label: i18n.customizeTab },
] as const;

const CustomTab = ({ label, count }: { label: string; count?: number }) => {
	const tabAttributes = useTab();
	const shouldRenderCount = !!count && count > 0;
	return (
		<Pressable
			{...tabAttributes}
			xcss={cx(styles.customTab, tabAttributes['aria-selected'] && styles.selectedTab)}
		>
			<Inline alignInline="center" alignBlock="center" space="space.050">
				<Box>{label}</Box>
				{shouldRenderCount && (
					<Text color="color.text.subtlest" size="small" testId="briefing-sources-count">
						{count}
					</Text>
				)}
			</Inline>
		</Pressable>
	);
};

const MinimizedPlayer = ({
	expandPopup,
	audioRef,
	isLoading,
}: MinimizedPlayerProps): JSX.Element => {
	return (
		<Pressable
			xcss={styles.minimizedWrapper}
			onClick={expandPopup}
			testId="briefings-minimized-player"
		>
			<Box padding="space.050">
				<AudioTimeIndicator audioRef={audioRef} isLoading={isLoading} variant="blue" />
			</Box>
		</Pressable>
	);
};

const getLengthMessage = (length: CustomLengthType) => {
	switch (length) {
		case CustomLength.SHORT:
			return i18n.lengthShort;
		case CustomLength.MEDIUM:
			return i18n.lengthMedium;
		default:
			return i18n.lengthLong;
	}
};

const getToneMessage = (tone: CustomToneType): MessageDescriptor | undefined => {
	switch (tone) {
		case CustomTone.CONVERSATIONAL:
			return i18n.toneConversational;
		case CustomTone.PLAYFUL:
			return i18n.tonePlayful;
		case CustomTone.SURPRISE:
			return undefined;
		default:
			return i18n.toneProfessional;
	}
};

export const AudioBriefingPopupContentComponent = ({
	closePopup,
	audioRef,
	isPopupMinimized,
	setIsPopupMinimized,
	showChangeboarding,
	handleCloseChangeboarding,
}: AudioBriefingPopupContentComponentProps) => {
	const { formatMessage } = useIntl();

	const [
		{
			activeContent,
			additionalResources,
			customization,
			isPlaying,
			error,
			isLoading,
			streamEnded,
			hasFetched,
			lastGeneratedBriefingConfig,
			playbackSpeed,
			activeFeature,
		},
		{ fetchBriefing },
	] = useAudioBriefing();

	const contentTitle = activeContent?.contentTitle;

	const { trackAIResultView, trackAIInteractionInit, trackAIResultAction } =
		useAIEventsInstrumentation();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendAnalyticsUIEvent = useCallback(
		(actionSubjectId: string, additionalAttributes?: Record<string, string | boolean>) => {
			const attributes = {
				...additionalAttributes,
			};
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'audioBriefingPopup',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	// Fire experience tracking success when the audio finishes streaming
	useEffect(() => {
		const fetchSuccess = hasFetched && !error && streamEnded;

		if (fetchSuccess) {
			experienceTracker.succeed({
				name: AI_AUDIO_BRIEFING,
			});
		}
	}, [hasFetched, error, streamEnded, experienceTracker]);

	// Fire AI success when the audio starts playing
	useEffect(() => {
		if (hasFetched && !isLoading && !error) {
			trackAIResultView({
				attributes: getSharedAttributes({
					customization: lastGeneratedBriefingConfig?.customization,
					additionalResources: lastGeneratedBriefingConfig?.additionalResources,
					playbackSpeed,
					hasFetched,
					activeFeature,
				}),
			});
			AI_AUDIO_BRIEFING_INTERACTION_METRIC.stop();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasFetched, isLoading, error]);

	const handleFetchContent = () => {
		experienceTracker.start({
			name: AI_AUDIO_BRIEFING,
			attributes: {
				contentId: activeContent?.id,
			},
		});
		AI_AUDIO_BRIEFING_INTERACTION_METRIC.start();
		fetchBriefing();

		trackAIInteractionInit({
			attributes: getSharedAttributes({
				customization,
				additionalResources,
				playbackSpeed,
				hasFetched,
				activeFeature: AudioFeature.BRIEFING,
			}),
		});
	};

	if (error) {
		return (
			<AudioBriefingErrorComponent
				error={error}
				audioRef={audioRef}
				closePopup={closePopup}
				onTryAgain={handleFetchContent}
			/>
		);
	}

	if (showChangeboarding) {
		return (
			<AudioBriefingChangeboarding
				closePopup={closePopup}
				handleFetchContent={handleFetchContent}
				onCloseChangeboarding={handleCloseChangeboarding}
			/>
		);
	}

	const { customization: lastGeneratedBriefingCustomization } = lastGeneratedBriefingConfig || {};

	const lengthMessage = getLengthMessage(
		lastGeneratedBriefingCustomization?.length || customization.length,
	);
	const toneMessage = getToneMessage(
		lastGeneratedBriefingCustomization?.tone || customization.tone,
	);
	const translatedLength = formatMessage(lengthMessage);
	const translatedTone = toneMessage ? formatMessage(toneMessage) : undefined;

	const sharedAttributes = getSharedAttributes({
		customization: lastGeneratedBriefingConfig?.customization,
		additionalResources: lastGeneratedBriefingConfig?.additionalResources,
		playbackSpeed,
		hasFetched,
		activeFeature,
	});

	const handleClose = () => {
		if (hasFetched && !isLoading && !error) {
			const audioClosedAttributes = {
				audioLengthMs: getAudioLengthMs(audioRef),
				audioCurrentTimeMs: getAudioCurrentTimeMs(audioRef),
				percentCompleted: getAudioProgress(audioRef),
				reason: 'closed from popup controls',
			};
			closePopup({
				...audioClosedAttributes,
			});
		} else {
			closePopup();
		}
	};

	const handleExpand = () => {
		trackAIResultAction('audioExpandPopup', {
			attributes: sharedAttributes,
		});
		setIsPopupMinimized(false);
	};

	const handleMinimize = () => {
		trackAIResultAction('audioMinimizePopup', {
			attributes: sharedAttributes,
		});
		setIsPopupMinimized(true);
	};

	const handleTabChange = (index: number) => {
		const tabName = AUDIO_TABS[index].key;

		sendAnalyticsUIEvent('audioSelectTab', {
			tab: tabName,
			hasFetched,
		});
	};

	const smartCardProviderProps = getSmartCardRenderers();

	return (
		<>
			{isPopupMinimized ? (
				<MinimizedPlayer
					expandPopup={handleExpand}
					audioRef={audioRef}
					isPlaying={isPlaying}
					isLoading={isLoading}
				/>
			) : (
				<Box xcss={styles.wrapper}>
					<Stack>
						<Box padding="space.200">
							<Inline grow="fill" space="space.150" alignBlock="start">
								<Stack xcss={styles.headerTitle}>
									<Heading size="small">
										{formatMessage(i18n.briefingTitle, { contentTitle })}
									</Heading>
									{fg('confluence_briefings_byline') && (
										<Text color="color.text.subtlest" size="small" maxLines={1}>
											{formatMessage(i18n.briefingBylineTemplate, {
												length: translatedLength,
												tone: translatedTone,
											})}
										</Text>
									)}
								</Stack>

								{/* Renders a close button if the audio is not playing,
								otherwise renders a minimize button */}
								<AudioBriefingPopupControlButton
									isPlaying={isPlaying}
									minimizePopup={handleMinimize}
									closePopup={handleClose}
								/>
							</Inline>

							<Tabs id="audio-tabs" onChange={handleTabChange}>
								<div css={tabsListStyles}>
									<TabList>
										{AUDIO_TABS.map((tab) => (
											<CustomTab
												key={tab.key}
												label={formatMessage(tab.label)}
												count={tab.key === 'sources' ? additionalResources.length : undefined}
											/>
										))}
									</TabList>
								</div>
								<TabPanel>
									<AudioBriefingPopupListenPanel
										audioRef={audioRef}
										handleFetchContent={handleFetchContent}
									/>
								</TabPanel>
								<TabPanel>
									<ConfluenceSmartCardProvider {...smartCardProviderProps}>
										<AudioBriefingPopupSourcesPanel audioRef={audioRef} />
									</ConfluenceSmartCardProvider>
								</TabPanel>
								<TabPanel>
									<AudioBriefingPopupCustomizePanel audioRef={audioRef} />
								</TabPanel>
							</Tabs>
						</Box>
						<Box xcss={styles.footer}>
							<AudioBriefingPopupFooter activeContent={activeContent} />
						</Box>
					</Stack>
				</Box>
			)}
		</>
	);
};

export const AudioBriefingPopupContent = (
	passThroughProps: AudioBriefingPopupContentProps,
): JSX.Element => {
	const [
		{
			stream,
			playbackSpeed,
			isLoading,
			customization,
			additionalResources,
			hasFetched,
			activeFeature,
		},
		{ pauseContent, resumeContent, endContent, setContentError, setStreamEnded },
	] = useAudioBriefing();
	const { audioRef } = useAudioStream(stream, setContentError, setStreamEnded);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { trackAIResultAction } = useAIEventsInstrumentation();

	const autoplayContent = async () => {
		if (!audioRef.current) {
			return;
		}

		await audioRef.current.play();
	};

	const fireContentBufferEvent = () => {
		trackAIResultAction('audioContentBuffer', {
			attributes: getSharedAttributes({
				customization,
				additionalResources,
				playbackSpeed,
				hasFetched,
				activeFeature,
			}),
		});
	};

	useAudioEventListener({
		audioRef,
		onContentPaused: pauseContent,
		onContentResumed: resumeContent,
		onContentEnded: endContent,
		onContentCanPlay: autoplayContent,
		onContentBuffer: fireContentBufferEvent,
	});

	// TODO: Remove this useEffect once autoplay logic is refactored
	// https://hello.jira.atlassian.cloud/browse/CCI-4396
	useEffect(() => {
		const audio = audioRef.current;
		if (audio && !isLoading) {
			audio.playbackRate = playbackSpeed;
		}
	}, [playbackSpeed, audioRef, isLoading]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'audioBriefingPopup',
			},
		}).fire();
		// This is only needed for the first render of the popup
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Box xcss={styles.popupStyles}>
			{/* eslint-disable-next-line @atlassian/a11y/media-has-caption -- The summary is already available in text so the track isn't very necessary */}
			<audio data-testid="briefings-audio" ref={audioRef} />
			<AudioBriefingPopupContentComponent audioRef={audioRef} {...passThroughProps} />
		</Box>
	);
};
